/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useState } from "react";
import ProfileSectionAction from "./ProfileSectionAction";

function FieldHint({ label, children }) {
  return (
    <div className="group relative mb-2 hidden sm:inline-flex">
      <button
        type="button"
        className="text-xs font-semibold uppercase tracking-wide text-slate-500 outline-none transition hover:text-[#01767A] focus:text-[#01767A]"
      >
        {label}
      </button>

      <div className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 h-4 w-px -translate-x-1/2 bg-[#24C3BE] opacity-0 transition-opacity delay-500 duration-200 group-hover:opacity-100 group-focus-within:opacity-100" />

      <div
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-30 mt-5 hidden w-72 -translate-x-1/2 rounded-2xl border border-[#24C3BE] bg-white p-3 text-center text-[11px] font-normal normal-case leading-tight tracking-normal text-[#374049] opacity-0 shadow-lg transition-opacity delay-500 duration-200 group-hover:opacity-100 group-focus-within:opacity-100 sm:block"
      >
        {children}
      </div>
    </div>
  );
}

export default function ProfileTaxonomyChallenge({
  isOwnProfile,
  sectionLoading,
  sectionProgress,
  SectionRefreshOverlay,
  quizType,
  setQuizType,
  questionCount,
  setQuestionCount,
  filterType,
  setFilterType,
  filterValue,
  setFilterValue,
  availableSpecies,
  filteredOptions,
  friendCollectionOptions = [],
  friendCollectionsLoading = false,
  quizFriendUID,
  setQuizFriendUID,
  startQuiz,
}) {
  const [challengeInfoOpen, setChallengeInfoOpen] = useState(false);
  const isFriendCollectionFilter = filterType === "friend-collection";
  const selectedFriend = friendCollectionOptions.find(
    (friend) => friend.uid === quizFriendUID,
  );
  const selectionOptions = isFriendCollectionFilter
    ? selectedFriend?.collections || []
    : filteredOptions;
  const filterSelectionPending =
    Boolean(filterType) &&
    (!filterValue || (isFriendCollectionFilter && !quizFriendUID));

  const maximumQuestions =
    availableSpecies.length === 0 &&
    (sectionLoading.quiz || filterSelectionPending)
      ? Math.max(10, questionCount || 10)
      : Math.max(1, availableSpecies.length || 1);
  const [questionDraft, setQuestionDraft] = useState(() =>
    String(Math.min(Math.max(1, questionCount || 1), maximumQuestions)),
  );
  const numericQuestionDraft = Number(questionDraft);
  const questionDraftIsValid =
    /^\d+$/.test(questionDraft) &&
    numericQuestionDraft >= 1 &&
    numericQuestionDraft <= maximumQuestions;

  useEffect(() => {
    const nextQuestionCount = Math.min(
      Math.max(1, questionCount || 1),
      maximumQuestions,
    );

    if (questionCount !== nextQuestionCount) {
      setQuestionCount(nextQuestionCount);
    }

    setQuestionDraft(String(nextQuestionCount));
  }, [maximumQuestions, questionCount, setQuestionCount]);

  function updateQuestionCount(rawValue) {
    const digitsOnly = String(rawValue || "").replace(/\D/g, "");
    const withoutLeadingZeros = digitsOnly.replace(/^0+(?=\d)/, "");

    if (!withoutLeadingZeros) {
      setQuestionDraft("");
      return;
    }

    const numericValue = Number(withoutLeadingZeros);

    if (numericValue > maximumQuestions) {
      setQuestionDraft(String(maximumQuestions));
      setQuestionCount(maximumQuestions);
      return;
    }

    setQuestionDraft(withoutLeadingZeros);

    if (numericValue >= 1) {
      setQuestionCount(numericValue);
    }
  }

  function commitQuestionCount() {
    if (questionDraftIsValid) return;

    setQuestionDraft("1");
    setQuestionCount(1);
  }

  return (
    <div
      className={`${isOwnProfile ? "lg:col-span-8" : "lg:col-span-10"} relative bg-white rounded-3xl shadow-lg border border-[#93ca87]/40`}
    >
      <SectionRefreshOverlay
        show={sectionLoading.quiz}
        progress={sectionProgress.quiz}
        label="Refreshing quiz data"
      />

      <div className="bioindex-quiz-heading relative px-6 pt-5 pb-2">
        <div className="bioindex-section-heading bioindex-quiz-title-row">
          <h2 className="bioindex-profile-panel-heading">Taxonomy Challenge</h2>
          <div className="bioindex-section-actions">
            <ProfileSectionAction
              kind="info"
              label="About Taxonomy Challenge"
              active={challengeInfoOpen}
              onClick={() => setChallengeInfoOpen((open) => !open)}
            />
          </div>
        </div>
        {challengeInfoOpen && (
          <div className="bioindex-quiz-about-popup" role="note">
            <button
              type="button"
              className="bioindex-quiz-about-close"
              onClick={() => setChallengeInfoOpen(false)}
              aria-label="Close Taxonomy Challenge information"
            >
              ×
            </button>
            <strong>About Taxonomy Challenge</strong>
            <p className="mt-2">
              Test your identification skills with quizzes based on your photos.
              Choose what to identify, set the number of questions, and optionally
              filter by a taxonomic group, your collections, or a friend's collection.
            </p>
          </div>
        )}
      </div>

      <div className="bioindex-quiz-body px-6 pb-6 pt-3">
        <div className="bioindex-quiz-fields grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(115px,0.43fr)]">
          <div className="relative min-w-0">
            <FieldHint label="Challenge">
              Choose what each question asks you to identify.
            </FieldHint>

            <select
              aria-label="Challenge type"
              value={quizType}
              onChange={(e) => setQuizType(e.target.value)}
              className="h-11 w-full min-w-0 rounded-xl border border-[#e7ddcf] bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15"
            >
              <option value="scientific-from-photo">Scientific name from photo</option>
              <option value="common-from-photo">Common name from photo</option>
              <option value="genus-from-photo">Genus from photo</option>
              <option value="family-from-photo">Family from photo</option>
              <option value="photo-from-scientific">Photo from scientific name</option>
              <option value="photo-from-common">Photo from common name</option>
            </select>
          </div>

          <div className="relative min-w-0">
            <FieldHint label="Questions">
              Set the round length, up to the number of available species.
            </FieldHint>
            <input
              aria-label="Number of questions"
              type="number"
              min="1"
              max={maximumQuestions}
              step="1"
              value={questionDraft}
              onFocus={(event) => event.currentTarget.select()}
              onChange={(event) => updateQuestionCount(event.target.value)}
              onBlur={commitQuestionCount}
              onKeyDown={(event) => {
                if (["e", "E", "+", "-", "."].includes(event.key)) {
                  event.preventDefault();
                }
              }}
              className="h-11 w-full min-w-0 rounded-xl border border-[#e7ddcf] bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15"
            />
            <div className="mt-1 text-[11px] text-slate-400">
              Max {availableSpecies.length || 0}
            </div>
          </div>
        </div>

        <div
          className={`bioindex-quiz-filter-controls ${
            isFriendCollectionFilter
              ? "bioindex-quiz-filter-three"
              : filterType
                ? "bioindex-quiz-filter-two"
                : "bioindex-quiz-filter-one"
          }`}
        >
          <div className="min-w-0">
            <FieldHint label="Filter">
              Focus the quiz by taxonomy, collection, or a friend's collection.
            </FieldHint>
            <select
              aria-label="Quiz filter"
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setFilterValue("");
                setQuizFriendUID("");
              }}
              className="bioindex-quiz-filter-select"
            >
              <option value="">No filter</option>
              <option value="kingdom">Kingdom</option>
              <option value="phylum">Phylum</option>
              <option value="class">Class</option>
              <option value="order">Order</option>
              <option value="family">Family</option>
              <option value="genus">Genus</option>
              <option value="collection">Collection</option>
              <option value="friend-collection">Friend's Collection</option>
            </select>
          </div>

          {isFriendCollectionFilter && (
            <div className="min-w-0">
              <label className="bioindex-quiz-filter-label" htmlFor="bioindex-quiz-friend">
                Friend
              </label>
              <select
                id="bioindex-quiz-friend"
                value={quizFriendUID}
                onChange={(event) => {
                  setQuizFriendUID(event.target.value);
                  setFilterValue("");
                }}
                disabled={friendCollectionsLoading}
                className="bioindex-quiz-filter-select"
              >
                <option value="">
                  {friendCollectionsLoading
                    ? "Loading friends..."
                    : friendCollectionOptions.length
                      ? "Choose a friend..."
                      : "No friend collections available"}
                </option>
                {friendCollectionOptions.map((friend) => (
                  <option key={friend.uid} value={friend.uid}>
                    {friend.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {filterType && (
            <div className="min-w-0">
              <label className="bioindex-quiz-filter-label" htmlFor="bioindex-quiz-filter-value">
                {isFriendCollectionFilter ? "Collection" : `Choose ${filterType}`}
              </label>
              <select
                id="bioindex-quiz-filter-value"
                value={filterValue}
                onChange={(event) => setFilterValue(event.target.value)}
                disabled={isFriendCollectionFilter && !quizFriendUID}
                className="bioindex-quiz-filter-select"
              >
                <option value="">
                  {isFriendCollectionFilter && !quizFriendUID
                    ? "Choose a friend first"
                    : "Choose..."}
                </option>
                {selectionOptions.map((opt) => {
                  const rawLabel = String(opt || "");
                  const preserveCapitalization =
                    filterType === "collection" || isFriendCollectionFilter;
                  const label = preserveCapitalization
                    ? rawLabel
                    : rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1).toLowerCase();
                  return (
                    <option key={opt} value={opt}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-[#93CA87]/30 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-slate-500">
            {availableSpecies.length === 0
              ? "No available species for this challenge."
              : `${availableSpecies.length} species available for this challenge.`}
          </div>

          <button
            type="button"
            onClick={startQuiz}
            disabled={availableSpecies.length === 0 || !questionDraftIsValid}
            className="rounded-xl bg-[#01767a] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#025f62] disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            Start Quiz
          </button>
        </div>
      </div>
    </div>
  );
}
